import { FinancialPlanAccounts } from '../types/financialPlan';

/** IRS limit: employer SEP contribution is at most 25% of net self-employment income. */
export const SEP_IRA_MAX_INCOME_PCT = 25;

/** 2026 §415 annual addition limit — keep in sync with IRS_LIMITS_2026.sepIra. */
export const SEP_IRA_IRS_CAP_ANNUAL = 69000;

function clampNonNeg(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** Maximum annual SEP IRA employer contribution for a gross income level (today's $). */
export function sepIraAnnualMax(grossIncomeTodayAnnual: number): number {
  const gross = clampNonNeg(grossIncomeTodayAnnual);
  return Math.min(gross * (SEP_IRA_MAX_INCOME_PCT / 100), SEP_IRA_IRS_CAP_ANNUAL);
}

/** Annual SEP contribution from employer % of gross income, capped at IRS limits. */
export function sepIraAnnualFromPct(grossIncomeTodayAnnual: number, employerIncomePct: number): number {
  const gross = clampNonNeg(grossIncomeTodayAnnual);
  const pct = clampNonNeg(employerIncomePct);
  const desired = gross * (pct / 100);
  return Math.min(desired, sepIraAnnualMax(gross));
}

export function sepIraMonthlyFromPct(grossIncomeTodayAnnual: number, employerIncomePct: number): number {
  return sepIraAnnualFromPct(grossIncomeTodayAnnual, employerIncomePct) / 12;
}

export function sepIraIncomePctFromMonthly(
  grossIncomeTodayAnnual: number,
  monthlyContribution: number,
): number {
  const gross = clampNonNeg(grossIncomeTodayAnnual);
  if (gross <= 0) return 0;
  const annual = clampNonNeg(monthlyContribution) * 12;
  const capped = Math.min(annual, sepIraAnnualMax(gross));
  return (capped / gross) * 100;
}

export function sepIraMonthlyFromAccount(
  grossIncomeTodayAnnual: number,
  account: FinancialPlanAccounts['k401'],
  mode: 'percent' | 'dollar',
): number {
  if (mode === 'percent') {
    return sepIraMonthlyFromPct(grossIncomeTodayAnnual, account.contributionPct ?? 0);
  }
  const maxMonthly = sepIraAnnualMax(grossIncomeTodayAnnual) / 12;
  return Math.min(clampNonNeg(account.monthlyContribution), maxMonthly);
}
